import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clientsAPI, transactionsAPI, loyaltyAPI } from '../services/api';
import { Plus, Search, Upload, Eye, X, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import Pagination from '../components/Pagination';
import SegmentFilterPanel from '../components/SegmentFilterPanel';
import toast from 'react-hot-toast';

const PER_PAGE = 20;


function ImportModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [status, setStatus] = useState(null);


  useEffect(() => {
    if (!taskId) return;
    const interval = setInterval(async () => {
      try {
        const res = await clientsAPI.importStatus(taskId);
        setProgress(res.data.progress);
        setStatus(res.data.status);
        if (res.data.status === 'success' || res.data.status === 'failed') {
          clearInterval(interval);
          setLoading(false);
          if (res.data.status === 'success') {
            const p = res.data.progress;
            toast.success(`Создано: ${p?.created || 0}, обновлено: ${p?.updated || 0}`);
            onDone();
          } else {
            toast.error(res.data.message || 'Ошибка импорта');
          }
        }
      } catch { clearInterval(interval); setLoading(false); }
    }, 2000);
    return () => clearInterval(interval);
  }, [taskId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Выберите файл'); return; }
    setLoading(true);
    setProgress(null);
    setStatus(null);
    try {
      const res = await clientsAPI.importFile(file, updateExisting);
      setTaskId(res.data.task_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка импорта');
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <h2 className="modal-title">Импорт клиентов</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Файл (.xlsx или .csv)</label>
            <input type="file" accept=".xlsx,.csv" style={{ color: 'var(--text-secondary)', fontSize: 13 }}
              onChange={e => setFile(e.target.files[0])} />
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, margin: '8px 0 12px' }}>
            Колонки: phone, email, имя, фамилия, отчество, пол, день рождения, уровень, баланс, рассылки.
            <br/>Клиенты без телефона загрузятся по email. Дубли определяются по телефону и email.
          </p>
          <label className="form-check">
            <input type="checkbox" checked={updateExisting} onChange={e => setUpdateExisting(e.target.checked)} />
            Обновлять существующих
          </label>
          {loading && (
            <div style={{ padding: 14, borderRadius: 'var(--radius-sm)', background: 'var(--bg-secondary)', border: '1px solid var(--border)', margin: '16px 0', fontSize: 13 }}>
              <div style={{ marginBottom: 8 }}>
                {status === 'running' ? '⏳ Импорт выполняется...' : '⏳ Запуск...'}
              </div>
              {progress && (
                <>
                  <div>Создано: <strong style={{ color: 'var(--success)' }}>{progress.created || 0}</strong></div>
                  <div>Обновлено: <strong>{progress.updated || 0}</strong></div>
                  <div>Пропущено: <strong>{progress.skipped || 0}</strong></div>
                  {progress.errors?.length > 0 && (
                    <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 6 }}>
                      Ошибки: {progress.errors.length}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          {!loading && progress && status === 'success' && (
            <div style={{ padding: 14, borderRadius: 'var(--radius-sm)', background: 'var(--bg-secondary)', border: '1px solid var(--border)', margin: '16px 0', fontSize: 13 }}>
              <div style={{ color: 'var(--success)', fontWeight: 600, marginBottom: 6 }}>Импорт завершён</div>
              <div>Создано: <strong>{progress.created || 0}</strong></div>
              <div>Обновлено: <strong>{progress.updated || 0}</strong></div>
              <div>Пропущено: <strong>{progress.skipped || 0}</strong></div>
              {progress.errors?.length > 0 && progress.errors.slice(0, 10).map((e, i) => (
                <div key={i} style={{ fontSize: 11, color: 'var(--danger)' }}>Строка {e.row}: {e.error}</div>
              ))}
            </div>
          )}
          {!loading && status === 'failed' && (
            <div style={{ padding: 14, borderRadius: 'var(--radius-sm)', background: 'var(--bg-secondary)', border: '1px solid var(--danger)', margin: '16px 0', fontSize: 13, color: 'var(--danger)' }}>
              Ошибка импорта
            </div>
          )}
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Закрыть</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !file}>
              {loading ? 'Импорт...' : 'Импортировать'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function CreateClientModal({ tiers, onClose, onCreated }) {
  const [form, setForm] = useState({
    phone: '', name: '', email: '', gender: '', birthdate: '', consent_sms: false, consent_email: false, loyalty_tier: '', balance: 0,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = { ...form, loyalty_tier: form.loyalty_tier || null, balance: parseFloat(form.balance) || 0, gender: form.gender || null, birthdate: form.birthdate || null };
      await clientsAPI.create(data);
      toast.success('Клиент создан');
      onCreated(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h2 className="modal-title">Новый клиент</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Телефон *</label>
            <input className="form-input" required placeholder="+79001234567"
              value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Имя</label>
            <input className="form-input" placeholder="Иван Петров"
              value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" placeholder="ivan@example.com"
              value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Пол</label>
              <select className="form-input" value={form.gender} onChange={e => setForm({ ...form, gender: e.target.value })}>
                <option value="">Не указан</option>
                <option value="male">Мужской</option>
                <option value="female">Женский</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Дата рождения</label>
              <input className="form-input" type="date" value={form.birthdate} onChange={e => setForm({ ...form, birthdate: e.target.value })} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Уровень лояльности</label>
              <select className="form-input" value={form.loyalty_tier}
                onChange={e => setForm({ ...form, loyalty_tier: e.target.value })}>
                <option value="">По умолчанию</option>
                {tiers.map(t => <option key={t.name} value={t.name}>{t.display_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Начальный баланс</label>
              <input className="form-input" type="number" min="0" step="0.01"
                value={form.balance} onChange={e => setForm({ ...form, balance: e.target.value })} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-check">
              <input type="checkbox" checked={form.consent_sms} onChange={e => setForm({ ...form, consent_sms: e.target.checked })} />
              Согласие на SMS
            </label>
          </div>
          <div className="form-group">
            <label className="form-check">
              <input type="checkbox" checked={form.consent_email} onChange={e => setForm({ ...form, consent_email: e.target.checked })} />
              Согласие на Email
            </label>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? '...' : 'Создать'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}


function ClientCard({ client, tiers, onClose, onUpdated }) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: client.name || '', email: client.email || '',
    gender: client.gender || '', birthdate: client.birthdate || '',
    consent_sms: client.consent_sms, consent_email: client.consent_email,
    loyalty_tier: client.loyalty_tier,
  });
  const [bonusForm, setBonusForm] = useState({ amount: '', comment: '', expires_in_days: '' });
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    transactionsAPI.history(client.id, 0, 20).then(res => setHistory(res.data.items)).catch(() => {});
  }, [client.id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await clientsAPI.update(client.id, form);
      toast.success('Клиент обновлён');
      setEditing(false);
      onUpdated();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
    finally { setSaving(false); }
  };

  const handleBonus = async (type) => {
    const amount = parseFloat(bonusForm.amount);
    if (!amount || amount <= 0) { toast.error('Укажите сумму'); return; }
    if (!bonusForm.comment) { toast.error('Укажите комментарий'); return; }
    try {
      const data = { phone: client.phone, amount, comment: bonusForm.comment };
      if (type === 'accrue' && bonusForm.expires_in_days) {
        data.expires_in_days = parseInt(bonusForm.expires_in_days);
      }
      if (type === 'accrue') await transactionsAPI.accrue(data);
      else await transactionsAPI.deduct(data);
      toast.success(type === 'accrue' ? `+${amount} баллов` : `−${amount} баллов`);
      setBonusForm({ amount: '', comment: '', expires_in_days: '' });
      onUpdated();
      transactionsAPI.history(client.id, 0, 20).then(res => setHistory(res.data.items));
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
  };

  const typeLabel = (t) => {
    const key = (t || '').toLowerCase();
    const map = {
      accrual: { text: '+', cls: 'badge-success' }, deduction: { text: '−', cls: 'badge-danger' },
      hold: { text: '❄', cls: 'badge-warning' }, hold_confirm: { text: '✓', cls: 'badge-info' },
      hold_cancel: { text: '↩', cls: 'badge-success' }, expiration: { text: '🔥', cls: 'badge-danger' },
    };
    return map[key] || { text: t, cls: 'badge-info' };
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 620, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 className="modal-title" style={{ margin: 0 }}>Карточка клиента</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><X size={20} /></button>
        </div>


        <div className="card-grid" style={{ marginBottom: 20 }}>
          <div className="stat-card">
            <div className="stat-card-label">Баланс</div>
            <div className="stat-card-value" style={{ fontSize: 22 }}>{client.balance.toLocaleString('ru')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-label">Заморожено</div>
            <div className="stat-card-value" style={{ fontSize: 22 }}>{client.held_balance.toLocaleString('ru')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-label">Доступно</div>
            <div className="stat-card-value" style={{ fontSize: 22 }}>{(client.balance - client.held_balance).toLocaleString('ru')}</div>
          </div>
        </div>


        {editing ? (
          <div style={{ marginBottom: 20 }}>
            <div className="form-group">
              <label className="form-label">Имя</label>
              <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Пол</label>
                <select className="form-input" value={form.gender} onChange={e => setForm({ ...form, gender: e.target.value })}>
                  <option value="">Не указан</option>
                  <option value="male">Мужской</option>
                  <option value="female">Женский</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Дата рождения</label>
                <input className="form-input" type="date" value={form.birthdate} onChange={e => setForm({ ...form, birthdate: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Уровень</label>
              <select className="form-input" value={form.loyalty_tier} onChange={e => setForm({ ...form, loyalty_tier: e.target.value })}>
                {tiers.map(t => <option key={t.name} value={t.name}>{t.display_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-check"><input type="checkbox" checked={form.consent_sms} onChange={e => setForm({ ...form, consent_sms: e.target.checked })} /> SMS</label>
            </div>
            <div className="form-group">
              <label className="form-check"><input type="checkbox" checked={form.consent_email} onChange={e => setForm({ ...form, consent_email: e.target.checked })} /> Email</label>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-success btn-sm" onClick={handleSave} disabled={saving}>Сохранить</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>Отмена</button>
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: 20, fontSize: 13, lineHeight: 2 }}>
            <div><span style={{ color: 'var(--text-muted)' }}>Телефон:</span> <strong style={{ fontFamily: 'var(--font-mono)' }}>{client.phone}</strong></div>
            <div><span style={{ color: 'var(--text-muted)' }}>Имя:</span> {client.name || '—'}</div>
            <div><span style={{ color: 'var(--text-muted)' }}>Email:</span> {client.email || '—'}</div>
            <div><span style={{ color: 'var(--text-muted)' }}>Пол:</span> {client.gender === 'male' ? 'Мужской' : client.gender === 'female' ? 'Женский' : '—'}</div>
            <div><span style={{ color: 'var(--text-muted)' }}>Дата рождения:</span> {client.birthdate ? new Date(client.birthdate).toLocaleDateString('ru') : '—'}</div>
            <div><span style={{ color: 'var(--text-muted)' }}>Уровень:</span> <span className="badge badge-info">{client.loyalty_tier}</span></div>
            <div><span style={{ color: 'var(--text-muted)' }}>SMS:</span> {client.consent_sms ? '✓' : '—'} &nbsp; <span style={{ color: 'var(--text-muted)' }}>Email:</span> {client.consent_email ? '✓' : '—'}</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>Редактировать</button>
              <button className="btn btn-ghost btn-sm" onClick={() => window.open(`/orders?client_id=${client.id}`, '_blank')}>
                Все заказы
              </button>
              <button className="btn btn-danger btn-sm" onClick={async () => {
                if (!window.confirm(`Удалить клиента ${client.phone}? Все транзакции и заказы будут удалены.`)) return;
                try { await clientsAPI.delete(client.id); toast.success('Клиент удалён'); onClose(); onUpdated(); }
                catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
              }}>Удалить</button>
            </div>
          </div>
        )}


        <div style={{ padding: 16, background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12 }}>Операции с бонусами</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 80 }}>
              <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Сумма</label>
              <input className="form-input" type="number" min="0.01" step="0.01" placeholder="100"
                value={bonusForm.amount} onChange={e => setBonusForm({ ...bonusForm, amount: e.target.value })} />
            </div>
            <div style={{ flex: 2, minWidth: 140 }}>
              <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Комментарий</label>
              <input className="form-input" placeholder="Причина"
                value={bonusForm.comment} onChange={e => setBonusForm({ ...bonusForm, comment: e.target.value })} />
            </div>
            <div style={{ width: 100 }}>
              <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Сгорают (дн)</label>
              <input className="form-input" type="number" min="1" placeholder="∞"
                value={bonusForm.expires_in_days} onChange={e => setBonusForm({ ...bonusForm, expires_in_days: e.target.value })} />
            </div>
            <button className="btn btn-success btn-sm" onClick={() => handleBonus('accrue')} title="Начислить"><ArrowUpCircle size={14} /> +</button>
            <button className="btn btn-danger btn-sm" onClick={() => handleBonus('deduct')} title="Списать"><ArrowDownCircle size={14} /> −</button>
          </div>
        </div>


        {history.length > 0 && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Последние операции</div>
            <table className="table" style={{ fontSize: 12 }}>
              <tbody>
                {history.map(t => {
                  const tl = typeLabel(t.type);
                  return (
                    <tr key={t.id}>
                      <td style={{ padding: '6px 10px', width: 130 }}>{new Date(t.created_at).toLocaleString('ru')}</td>
                      <td style={{ padding: '6px 10px' }}><span className={`badge ${tl.cls}`}>{tl.text} {t.amount.toLocaleString('ru')}</span></td>
                      <td style={{ padding: '6px 10px', color: 'var(--text-muted)' }}>{t.comment || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


export default function ClientsPage() {
  const [searchParams] = useSearchParams();
  const clientIdFromUrl = searchParams.get('client_id');

  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [search, setSearch] = useState('');
  const [tiers, setTiers] = useState([]);
  const [filters, setFilters] = useState({
    segments: [], exclude_segments: [], loyalty_tiers: [], place_ids: [],
    min_balance: '', max_balance: '', min_total_spent: '', max_total_spent: '',
    min_orders: '', max_orders: '', gender: '', has_email: false, has_birthday_in_days: '',
  });

  const buildSF = () => {
    const sf = {};
    if (filters.segments?.length) sf.segments = filters.segments;
    if (filters.exclude_segments?.length) sf.exclude_segments = filters.exclude_segments;
    if (filters.loyalty_tiers?.length) sf.loyalty_tiers = filters.loyalty_tiers;
    if (filters.exclude_tiers?.length) sf.exclude_tiers = filters.exclude_tiers;
    if (filters.place_ids?.length) sf.place_ids = filters.place_ids;
    if (filters.exclude_place_ids?.length) sf.exclude_place_ids = filters.exclude_place_ids;
    ['min_balance','max_balance','min_total_spent','max_total_spent'].forEach(k => {
      if (filters[k] !== '' && filters[k] !== undefined) sf[k] = parseFloat(filters[k]);
    });
    ['min_orders','max_orders','has_birthday_in_days'].forEach(k => {
      if (filters[k] !== '' && filters[k] !== undefined) sf[k] = parseInt(filters[k]);
    });
    if (filters.gender) sf.gender = filters.gender;
    if (filters.has_email) sf.has_email = true;
    if (filters.consent_sms) sf.consent_sms = true;
    if (filters.consent_email) sf.consent_email = true;
    return sf;
  };

  const load = (p = page, q = search) => {
    const skip = (p - 1) * PER_PAGE;
    const sf = buildSF();
    const hasFilters = Object.keys(sf).length > 0;
    if (hasFilters) {
      clientsAPI.filtered({ skip, limit: PER_PAGE, search: q || null, segment_filter: sf })
        .then(res => { setClients(res.data.items); setTotal(res.data.total); });
    } else {
      clientsAPI.list(skip, PER_PAGE, q || null)
        .then(res => { setClients(res.data.items); setTotal(res.data.total); });
    }
  };

  useEffect(() => {
    load(page, search);
    loyaltyAPI.tiers().then(res => setTiers(res.data)).catch(() => {});
  }, [page]);


  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      load(1, search);
    }, 400);
    return () => clearTimeout(t);
  }, [search, JSON.stringify(filters)]);


  useEffect(() => {
    if (clientIdFromUrl) {
      clientsAPI.get(clientIdFromUrl).then(r => setSelectedClient(r.data)).catch(() => {});
    }
  }, [clientIdFromUrl]);


  const filtered = clients;

  const totalPages = Math.ceil(total / PER_PAGE);

  const tierDisplay = (name) => {
    const t = tiers.find(t => t.name === name);
    return t
      ? <span className="badge" style={{ background: t.color + '20', color: t.color }}>{t.display_name}</span>
      : <span className="badge badge-info">{name}</span>;
  };

  return (
    <div className="animate-in">
      <div className="page-header">
        <h1 className="page-title">Клиенты</h1>
        <p className="page-subtitle">Всего: {total}</p>
      </div>

      <div className="page-actions">
        <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input className="form-input" style={{ paddingLeft: 36 }} placeholder="Поиск..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}><Plus size={16} /> Добавить</button>
        <button className="btn btn-ghost" onClick={() => setShowImport(true)}><Upload size={16} /> Импорт</button>
      </div>

      <SegmentFilterPanel filters={filters} onChange={setFilters} />

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Телефон</th><th>Имя</th><th>Email</th><th>Пол</th><th>Д.Р.</th><th>Уровень</th><th>Баланс</th><th>SMS</th><th>Email</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedClient(c)}>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{c.phone}</td>
                <td>{c.name || '—'}</td>
                <td>{c.email || '—'}</td>
                <td>{c.gender === 'male' ? 'М' : c.gender === 'female' ? 'Ж' : '—'}</td>
                <td>{c.birthdate ? new Date(c.birthdate).toLocaleDateString('ru') : '—'}</td>
                <td>{tierDisplay(c.loyalty_tier)}</td>
                <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{c.balance.toLocaleString('ru')}</td>
                <td>{c.consent_sms ? '✓' : '—'}</td>
                <td>{c.consent_email ? '✓' : '—'}</td>
                <td>
                  <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); setSelectedClient(c); }}>
                    <Eye size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                {total === 0 ? 'Клиентов пока нет' : 'Ничего не найдено'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {showCreate && <CreateClientModal tiers={tiers} onClose={() => setShowCreate(false)} onCreated={() => load(page)} />}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onDone={() => load(page)} />}
      {selectedClient && <ClientCard client={selectedClient} tiers={tiers} onClose={() => setSelectedClient(null)} onUpdated={() => { load(page); clientsAPI.get(selectedClient.id).then(r => setSelectedClient(r.data)).catch(() => setSelectedClient(null)); }} />}
    </div>
  );
}